import React, { useState, useEffect, useRef, useMemo } from "react";
import ParticleEffect from "./common/ParticleEffect";
import Navbar from "./layout/Navbar";
import Footer from "./layout/Footer";
import HelloFOSS from "./pages/HelloFOSS";
import CodingCircuit from "./pages/CodingCircuit";
import Quiz from "./pages/Quiz";
import SSOLoading from "./pages/SSOLoading";
import Card from "./common/Card";
import TechNewsFeed from "./common/TechNewsFeed";

// Import all the required icons
import {
    Code,
    Laptop,
    Cpu,
    GitBranch,
    Trophy,
    Star,
    Users,
    Calendar,
    Lightbulb,
    MessageCircle,
    Github,
    Linkedin,
    Activity,
    Instagram,
    Zap,
    Brain,
    Globe,
    Search,
    Braces,
    Lock,
    Smartphone,
    Binary,
    Server,
    Terminal,
    Database,
    Code2,
    X
} from "lucide-react";

// import { 
//     WebsitePreviewComponents 
// } from './WnCCWebsiteOriginal';

// Helper function
import { generateCalendarUrl } from '../utils/helpers';

// Stats component for HomePage
const StatsCounter = ({ label, endValue, duration = 2000 }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
        let startTime;
        let animationFrame;

        const animate = (currentTime) => {
            if (!startTime) startTime = currentTime;
            const progress = (currentTime - startTime) / duration;

            if (progress < 1) {
                setCount(Math.floor(endValue * progress));
                animationFrame = requestAnimationFrame(animate);
            } else {
                setCount(endValue);
            }
        };

        animationFrame = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(animationFrame);
    }, [endValue, duration]);

    return (
        <div className="text-center p-4">
            <div className="text-4xl font-bold text-cyan-400 mb-2">{count}+</div>
            <div className="text-gray-300">{label}</div>
        </div>
    );
};
const LiveCodeEditor = () => {
    const [code, setCode] = useState("");
    const [output, setOutput] = useState("");
    const [language, setLanguage] = useState("javascript");
    const [isLoading, setIsLoading] = useState(false);
    const [pyodide, setPyodide] = useState(null);

    // Default starter code for each language
    const starterCode = useMemo(() => ({
        javascript:
            '// Try this JavaScript code:\nconsole.log("Hello World!");\n\n// Or try a function:\nfunction greet(name) {\n  return "Hello " + name + "!";\n}\n\nconsole.log(greet("Developer"));',
        python:
            '# Try this Python code:\nprint("Hello World!")\n\n# Or try some calculations:\ndef factorial(n):\n    if n == 0:\n        return 1\n    return n * factorial(n-1)\n\nprint(f"Factorial of 5 is {factorial(5)}")',
        cpp: '#include <iostream>\n\nint main() {\n    std::cout << "Hello World!" << std::endl;\n    \n    // Calculate factorial\n    int n = 5;\n    int factorial = 1;\n    for(int i = 1; i <= n; i++) {\n        factorial *= i;\n    }\n    std::cout << "Factorial of 5 is " << factorial << std::endl;\n    return 0;\n}',
    }), []);

    // Initialize code with starter code
    useEffect(() => {
        setCode(starterCode[language]);
    }, [language, starterCode]);

    // Initialize Pyodide
    useEffect(() => {
        const loadPyodide = async () => {
            if (language === "python" && !pyodide) {
                try {
                    const script = document.createElement("script");
                    script.src =
                        "https://cdn.jsdelivr.net/pyodide/v0.24.1/full/pyodide.js";
                    script.async = true;
                    document.body.appendChild(script);

                    script.onload = async () => {
                        const pyodideInstance = await window.loadPyodide({
                            indexURL: "https://cdn.jsdelivr.net/pyodide/v0.24.1/full/",
                        });
                        setPyodide(pyodideInstance);
                    };
                } catch (err) {
                    setOutput("Error: Failed to load Python runtime");
                }
            }
        };

        loadPyodide();
    }, [language, pyodide]);

    // Execute code based on language
    const executeCode = async () => {
        setIsLoading(true);
        setOutput("");

        try {
            switch (language) {
                case "javascript":
                    await executeJavaScript();
                    break;
                case "python":
                    await executePython();
                    break;
                case "cpp":
                    await executeCPP();
                    break;
                default:
                    setOutput("Unsupported language");
                    break;
            }
        } catch (err) {
            setOutput(`Error: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // JavaScript execution in sandbox
    const executeJavaScript = async () => {
        const originalConsoleLog = console.log;
        let output = [];

        try {
            console.log = (...args) => {
                output.push(
                    args
                        .map((arg) =>
                            typeof arg === "object" ? JSON.stringify(arg) : String(arg)
                        )
                        .join(" ")
                );
            };

            // eslint-disable-next-line no-new-func
            const sandbox = new Function("console", code);

            sandbox({ log: console.log });
            setOutput(output.join("\n"));
        } finally {
            console.log = originalConsoleLog;
        }
    };

    // Python execution using Pyodide
    const executePython = async () => {
        if (!pyodide) {
            setOutput("Python runtime is still loading... Please wait.");
            return;
        }

        try {
            pyodide.runPython(`
        import sys
        import io
        sys.stdout = io.StringIO()
      `);

            await pyodide.runPythonAsync(code);
            const stdout = pyodide.runPython("sys.stdout.getvalue()");
            setOutput(stdout);
        } catch (err) {
            setOutput(`Python Error: ${err.message}`);
        }
    };

    // C++ execution (placeholder)
    const executeCPP = async () => {
        try {
            setOutput(
                "C++ compilation is being implemented. This is a placeholder output."
            );
        } catch (err) {
            setOutput(`C++ Error: ${err.message}`);
        }
    };

    return (
        <div className="bg-gray-800/50 p-6 rounded-xl">
            <div className="mb-4 flex items-center justify-between">
                <h3 className="text-xl font-bold text-cyan-400">Live Code Editor</h3>
                <div className="flex gap-2">
                    <select
                        value={language}
                        onChange={(e) => {
                            setLanguage(e.target.value);
                            setCode(starterCode[e.target.value]);
                            setOutput("");
                        }}
                        className="px-3 py-1 bg-gray-700 text-cyan-400 rounded text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    >
                        <option value="javascript">JavaScript</option>
                        <option value="python">Python</option>
                        <option value="cpp">C++</option>
                    </select>
                    <button
                        onClick={() => setCode(starterCode[language])}
                        className="px-3 py-1 bg-gray-700 rounded text-sm text-cyan-400 hover:bg-gray-600"
                    >
                        Reset
                    </button>
                </div>
            </div>
            <textarea
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full bg-gray-900 text-cyan-400 font-mono p-4 rounded mb-4 h-32 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                spellCheck="false"
            />
            <button
                onClick={executeCode}
                disabled={isLoading || (language === "python" && !pyodide)}
                className="bg-cyan-500 text-white px-4 py-2 rounded hover:bg-cyan-600 transition-all duration-300 transform hover:-translate-y-1 disabled:opacity-50"
            >
                {isLoading ? "Running..." : "Run Code ▶"}
            </button>
            {output && (
                <div className="mt-4 p-4 bg-gray-900 rounded font-mono">
                    <pre className="text-green-400 whitespace-pre-wrap">{output}</pre>
                </div>
            )}
        </div>
    );
};

// Activity Feed component for HomePage
const ActivityFeed = () => {
    const activities = [
        { text: "Hello FOSS Begins", time: " 4th Oct" },
        { text: "Seasons of Code Begins", time: " 21st May" },
        { text: "Launch of DSA Bootcamp", time: "1st June" },
        { text: "Kickoff of the Learners’ Space", time: "9th June" }


    ];

    return (
        <div className="bg-gray-800/50 p-4 rounded-xl">
            <h3 className="text-xl font-bold text-cyan-400 mb-4">Recent Activity</h3>
            {activities.map((activity, index) => (
                <div
                    key={index}
                    className="flex items-center gap-3 mb-3 p-2 hover:bg-gray-700/30 rounded"
                >
                    <Activity className="text-cyan-400 w-4 h-4" />
                    <div>
                        <p className="text-white">{activity.text}</p>
                        <p className="text-gray-400 text-sm">{activity.time}</p>
                    </div>
                </div>
            ))}
        </div>
    );
};

// RiddleGame Component
const RiddleGame = () => {
    const riddles = [
        {
            question:
                "In a room of 23 people, what's the probability that at least two people share the same birthday? (Answer in percentage, rounded to nearest whole number)",
            answer: "51",
        },
        {
            question:
                "You have 8 identical balls, but 1 is slightly heavier. Using a balance scale, what's the minimum number of weighings needed to find the heavier ball?",
            answer: "2",
        },
        {
            question:
                "100 people standing in a circle in an order from 1 to 100. Person 1 has a sword. He kills the next person (2) and gives the sword to the next living person (3). All people do the same until only one survives. Which number lives?",
            answer: "73",
        },
        {
            question:
                "A snail climbs up a 10-foot wall. Each day it climbs up 3 feet, but slides down 2 feet at night. How many days will it take the snail to reach the top?",
            answer: "8",
        },
        {
            question:
                "You have a 5-liter jug and a 3-liter jug. How can you measure exactly 4 liters? Enter the number of steps required.",
            answer: "6",
        },
        {
            question:
                "In binary representation, how many numbers from 1 to 20 (inclusive) have exactly two 1's in their binary form?",
            answer: "6",
        },
        {
            question:
                "A message is encoded by shifting each letter by its position (1st letter shifted by 1, 2nd by 2, etc). 'HAL' becomes 'IBM'. What does 'BUG' become?",
            answer: "DIJ",
        },
        {
            question:
                "On a 3x3 grid, how many unique paths are there from top-left to bottom-right if you can only move right or down?",
            answer: "6",
        },
        {
            question:
                "In an array of integers from 1 to n, one number appears twice while one number is missing. If array sum is 55 and n is 10, what's the duplicate number?",
            answer: "7",
        },
        {
            question:
                "Given a fair coin, what's the expected number of flips needed to get two consecutive heads? (Round to one decimal place)",
            answer: "6",
        },
        {
            question: "How many trailing zeros are in 100 factorial (100!)?",
            answer: "24",
        },
        {
            question:
                "What's the maximum number of pieces you can get by cutting a circular pizza with 6 straight cuts?",
            answer: "22",
        },
        {
            question:
                "If you have 9 coins and one is counterfeit (lighter), minimum number of weighings on a balance scale to find it?",
            answer: "2",
        },
        {
            question:
                "How many different ways can you make change for $1 using standard US coins (1,5,10,25,50 cents)?",
            answer: "292",
        },
        {
            question:
                "In a sorted array of n distinct integers, if array[index] = index for some index, this index is called a magic index. For array [-5,-3,0,3,7], what's the magic index?",
            answer: "3",
        },
    ];

    const [currentRiddle, setCurrentRiddle] = useState(riddles[0]);
    const [userAnswer, setUserAnswer] = useState("");
    const [feedback, setFeedback] = useState("");
    const [streak, setStreak] = useState(0);
    const [usedRiddles, setUsedRiddles] = useState(new Set([0]));
    const [, setShowHint] = useState(false);

    const getNextRiddle = () => {
        let availableIndices = Array.from(
            { length: riddles.length },
            (_, i) => i
        ).filter((i) => !usedRiddles.has(i));

        if (availableIndices.length === 0) {
            setUsedRiddles(new Set([0]));
            availableIndices = Array.from({ length: riddles.length }, (_, i) => i);
        }

        const nextIndex =
            availableIndices[Math.floor(Math.random() * availableIndices.length)];
        setCurrentRiddle(riddles[nextIndex]);
        setUsedRiddles((prev) => new Set([...prev, nextIndex]));
        setShowHint(false);
    };

    const checkAnswer = () => {
        if (
            userAnswer.toLowerCase().trim() === currentRiddle.answer.toLowerCase()
        ) {
            setStreak((prev) => prev + 1);
            setFeedback("Correct! 🎉 Get ready for the next question...");
            setUserAnswer("");
            setShowHint(false);
            setTimeout(() => {
                setFeedback("");
                getNextRiddle();
            }, 2000);
        } else {
            setFeedback("Incorrect! Try again 💡");
            setStreak(0);
        }
    };

    return (
        <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-cyan-400">
                    Coding Interview Practice
                </h3>
                <div className="flex gap-4 items-center">
                    <span className="bg-cyan-500/20 px-3 py-1 rounded-full text-cyan-400">
                        Streak: {streak} 🔥
                    </span>
                    <button
                        onClick={() => {
                            setStreak(0);
                            setUserAnswer("");
                            setFeedback("");
                            setShowHint(false);
                            getNextRiddle();
                        }}
                        className="text-sm bg-gray-700 hover:bg-gray-600 text-gray-300 px-3 py-1 rounded-full transition-colors"
                    >
                        Skip
                    </button>
                </div>
            </div>
            <div className="bg-gray-800 p-4 rounded-lg mb-6">
                <p className="text-lg text-gray-200">{currentRiddle.question}</p>
            </div>
            <div className="flex gap-2">
                <input
                    type="text"
                    value={userAnswer}
                    onChange={(e) => setUserAnswer(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && checkAnswer()}
                    className="flex-1 bg-gray-700 rounded px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="Your answer..."
                />
                <button
                    onClick={checkAnswer}
                    className="bg-cyan-500 text-white px-4 py-2 rounded hover:bg-cyan-600 transition-colors"
                >
                    Submit
                </button>
            </div>
            {feedback && (
                <div
                    className={`mt-4 text-center ${feedback.includes("Correct") ? "text-green-400" : "text-cyan-400"
                        }`}
                >
                    {feedback}
                </div>
            )}
        </Card>
    );
};
const FeatureCard = ({ icon: Icon, title, description }) => (
    <Card className="text-center">
        <Icon className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
        <p className="text-gray-300">{description}</p>
    </Card>
);

// About Us Section
const AboutUs = () => (
    <div className="mt-20 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-900/50 pointer-events-none" />
        <h2 className="text-4xl font-bold text-center text-cyan-400 mb-8">
            About Us
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
            <Card>
                <h3 className="text-2xl font-bold text-cyan-400 mb-4">Who are we?</h3>
                <p className="text-gray-300">
                    The Web and Coding Club, one of the biggest clubs at IIT Bombay and
                    part of the Institute Technical Council, provides a gateway for
                    students to join the coding community. We offer mentorship and a
                    platform to help students enhance their coding skills, ensuring
                    everyone has the opportunity to learn and develop a passion for
                    coding. The secret to getting ahead is getting started, and we’re here
                    to give every student the right start.
                </p>
            </Card>
            <Card>
                <h3 className="text-2xl font-bold text-cyan-400 mb-4">Our Mission </h3>
                <p className="text-gray-300">
                    We aim to foster a culture of learning and innovation by empowering
                    students with the skills to excel in web development, competitive
                    coding, open-source contributions, and emerging technologies.
                </p>
            </Card>
            <Card>
                <h3 className="text-2xl font-bold text-cyan-400 mb-4">Our Vision</h3>
                <p className="text-gray-300">
                    Create an inclusive environment where students collaborate, innovate,
                    and grow together as problem-solvers and developers.
                </p>
            </Card>
        </div>
    </div>
);

const HomePage = () => {
    const [typewriterText, setTypewriterText] = useState("");
    const fullText = "Coders Together Strong";

    useEffect(() => {
        let index = 0;
        const interval = setInterval(() => {
            setTypewriterText(fullText.substring(0, index));
            index++;
            if (index > fullText.length) {
                setTimeout(() => {
                    index = 0;
                }, 2000);
            }
        }, 100);
        return () => clearInterval(interval);
    }, []);
    const features = [
        {
            icon: Brain,
            title: "Fast Paced-Courses",
            description:
                "Deep dive into topics like machine learning, Web Development, and more",
        },
        {
            icon: Globe,
            title: "Hackathons",
            description: "Compete with like minded-developers",
        },
        {
            icon: Zap,
            title: "Expert Talk Sessions",
            description: "Quick, intensive tech sessions",
        },
    ];
    return (
        <div className="space-y-12 relative">
            <ParticleEffect />

            <div className="text-center relative">
                <div className="absolute -top-20 -left-20 w-64 h-64 bg-cyan-500/20 rounded-full filter blur-3xl" />
                <div className="absolute top-0 right-0 w-32 h-32 sm:w-48 sm:h-48 md:w-64 md:h-64 bg-blue-500/20 rounded-full filter blur-3xl max-w-full max-h-full sm:max-w-[calc(100vw-2rem)] md:max-w-[calc(100vw-2rem)]" />
                <h1 className="text-6xl font-bold mb-4 bg-gradient-to-r from-cyan-400 to-blue-500 text-transparent bg-clip-text">
                    WnCC IIT Bombay
                </h1>

                <div className="h-8">
                    <span className="font-mono text-2xl text-cyan-400">
                        {typewriterText}
                        <span className="animate-pulse">_</span>
                    </span>
                </div>
            </div>

            <div className="grid md:grid-cols-4 gap-6">
                <StatsCounter label="SOC 2026 Participation" endValue={1210} />
                <StatsCounter label="Wiki Articles" endValue={110} />
                <StatsCounter label="Github Repos" endValue={90} />
                <StatsCounter label="Years since fomation" endValue={19} />
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
                {features.map((feature, index) => (
                    <FeatureCard key={index} {...feature} />
                ))}
            </div>

            <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-8">
                    <LiveCodeEditor />
                    <ActivityFeed />
                </div>
                <div className="space-y-8">
                    <RiddleGame />
                    <TechNewsFeed />
                </div>
            </div>

            <AboutUs />
        </div>
    );
};
const EventCard = ({ event }) => {
    const [isHovered, setIsHovered] = useState(false);

    const timeUntilEvent = () => {
        const now = new Date();
        const eventDate = new Date(event.date);
        const diff = eventDate - now;

        if (diff < 0) return "Event has passed";

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

        return `${days}d ${hours}h remaining`;
    };

    return (
        <Card
            className="transform transition-all duration-500 hover:scale-105"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className="relative overflow-hidden rounded-t-xl -mt-6 -mx-6 mb-6">
                <div className="h-48 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 flex items-center justify-center">
                    {event.icon}
                    {isHovered && (
                        <div className="absolute inset-0 bg-cyan-500/10 backdrop-blur-sm flex items-center justify-center">
                            <button className="bg-cyan-500 text-white px-6 py-2 rounded-full transform hover:scale-105 transition-transform">
                                Register Now
                            </button>
                        </div>
                    )}
                </div>
                <div className="absolute top-4 right-4 bg-cyan-500/90 text-white px-3 py-1 rounded-full text-sm">
                    {timeUntilEvent()}
                </div>
            </div>

            <h3 className="text-2xl font-semibold text-white mb-2">{event.title}</h3>
            <div className="flex items-center gap-2 text-cyan-300 mb-2">
                <Calendar className="w-4 h-4" />
                {new Date(event.date).toLocaleDateString("en-US", {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                })}
            </div>
            <p className="text-gray-300 mb-4">{event.description}</p>

            <div className="flex items-center gap-4 mb-4">
                <span className="text-gray-400 flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {event.participants || " "}
                </span>
                <span className="text-gray-400 flex items-center gap-1">
                    <Trophy className="w-4 h-4" />
                    {event.prize || "$1000"}
                </span>
            </div>

            <div className="flex gap-2">
                <a
                    href={generateCalendarUrl(event)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-cyan-500/20 rounded-lg hover:bg-cyan-500/30 transition-colors text-cyan-400"
                >
                    <Calendar className="w-4 h-4" />
                    Add to Calendar
                </a>
                <button className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors ml-auto">
                    Learn More
                </button>
            </div>
        </Card>
    );
};

// Timeline component for EventsPage
const EventTimeline = () => {
    const [selectedEvent, setSelectedEvent] = useState(null);

    const timelineItems = [
        {
            title: "Season Kickoff",
            date: "April 2026",
            description: "The new season kicks off with lots of enthusiasm",
            icon: <Star className="w-6 h-6" />,
            color: "from-green-500 to-emerald-700"
        },
        {
            title: "Seasons of Code",
            date: "May end to July",
            description: "Flagship Event, 8 Week-long",
            icon: <Cpu className="w-6 h-6" />,
            color: "from-blue-500 to-indigo-700"
        },
        {
            title: "DSA Bootcamp",
            date: "3rd June",
            description: "8-week Journey to master DSA!",
            icon: <Code className="w-6 h-6" />,
            color: "from-purple-500 to-pink-700"
        },
        {
            title: "Learners' Space",
            date: "10th June",
            description: "4-week long | Igniting passion, elevating skills & unlocking the future of tech!",
            icon: <Code className="w-6 h-6" />,
            color: "from-cyan-500 to-indigo-700"
        }
    ];

    return (
        <div className="relative mt-20">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-cyan-500/50 to-transparent" />
            {timelineItems.map((item, index) => (
                <div
                    key={index}
                    className={`relative flex items-center mb-12 ${index % 2 === 0 ? "flex-row" : "flex-row-reverse"
                        }`}
                    onMouseEnter={() => setSelectedEvent(index)}
                    onMouseLeave={() => setSelectedEvent(null)}
                >
                    <div
                        className={`w-1/2 ${index % 2 === 0 ? "pr-8 text-right" : "pl-8"}`}
                    >
                        <div
                            className={`p-6 rounded-xl bg-gradient-to-br ${item.color
                                } transform transition-all duration-300 ${selectedEvent === index ? "scale-105" : ""
                                }`}
                        >
                            <div className="flex items-center gap-3 mb-2 justify-end">
                                {item.icon}
                                <h3 className="text-xl font-bold text-white">{item.title}</h3>
                            </div>
                            <p className="text-gray-200">{item.date}</p>
                            <p className="text-gray-100 mt-2">{item.description}</p>
                        </div>
                    </div>
                    <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center justify-center">
                        <div
                            className={`w-4 h-4 rounded-full bg-cyan-400 transition-transform duration-300 ${selectedEvent === index ? "scale-150" : ""
                                }`}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
};

const EventsPage = () => {
    const [selectedFilter, setSelectedFilter] = useState("all");

    const events = [
        {
            title: 'DSA Bootcamp',
            date: '2026-06-03T20:00:00Z',
            endDate: '2026-06-28T20:30:00Z',
            description: 'A comprehensive Data Structures and Algorithms bootcamp designed to build a strong problem-solving foundation from scratch.',
            location: 'IIT Bombay Campus',
            icon: <Calendar className="w-16 h-16 text-cyan-400" />,
            category: 'Bootcamp',
            participants: '',
            prize: ''
        },
        {
            title: 'Learner Space',
            date: '2026-06-10T20:00:00Z',
            endDate: '2026-08-07T20:00:00Z',
            description: 'A 4-week long learning program designed to ignite passion, elevate skills, and unlock the future of tech for aspiring developers.',
            location: 'IIT Bombay Campus',
            icon: <Laptop className="w-16 h-16 text-cyan-400" />,
            category: 'Bootcamp',
            participants: '',
            prize: ''
        },
        {
            title: 'Seasons of Code',
            date: '2026-05-17T00:00:00Z',
            endDate: '2026-07-17T00:00:00Z',
            description: "The flagship event of WnCC, an 8-week long project-based learning extravaganza where participants build real-world applications and collaborate with peers to learn showcase their skills",
            location: 'IIT Bombay Campus',
            icon: <Lightbulb className="w-16 h-16 text-cyan-400" />,
            category: 'Project',
            participants: '',
            prize: ' '
        }
    ];


    const filteredEvents =
        selectedFilter === "all"
            ? events
            : events.filter((event) => event.category === selectedFilter);

    return (
        <div className="space-y-12 relative">
            {/* Background decorative elements */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/10 rounded-full filter blur-3xl" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full filter blur-3xl" />

            {/* Hero section */}
            <div className="text-center relative">
                <h2 className="text-5xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 text-transparent bg-clip-text mb-6">
                    Upcoming Events
                </h2>
                <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                    Join us for exciting workshops, competitions, and learning experiences
                </p>
            </div>

            {/* Filters */}
            <div className="flex justify-center gap-4">
                {["all", "competition", "workshop", "seminar"].map((filter) => (
                    <button
                        key={filter}
                        onClick={() => setSelectedFilter(filter)}
                        className={`px-6 py-2 rounded-full transition-all duration-300 ${selectedFilter === filter
                            ? "bg-cyan-500 text-white"
                            : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                            }`}
                    >
                        {filter.charAt(0).toUpperCase() + filter.slice(1)}
                    </button>
                ))}
            </div>

            {/* Events grid */}
            <div className="grid md:grid-cols-3 gap-8">
                {filteredEvents.map((event, index) => (
                    <EventCard key={index} event={event} />
                ))}
            </div>

            {/* Timeline section */}
            <div className="mt-20">
                <h3 className="text-3xl font-bold text-center text-cyan-400 mb-12">
                    Event Timeline
                </h3>
                <EventTimeline />
            </div>
        </div>
    );
};
const ResourcesPage = () => {
    // const [, setActiveCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);



    const resources = [
        {
            title: 'Getting Started with Webdev',
            category: 'tutorials',
            difficulty: 'LEVEL::INTERMEDIATE',
            duration: '4 Weeks',
            author: 'WnCC Team',
            icon: <Code className="w-8 h-8" />,
            color: 'from-purple-500 to-pink-500',
            description: '> Frontend Development\n> React and Django Architecture\n> UI/UX Engineering',
            tags: ['react', 'javascript', 'frontend'],

            link: 'https://github.com/wncc/Web-Dev-LS-26'
        },
        {
            title: 'Data Structures and Algorithms',
            category: 'courses',
            difficulty: 'LEVEL::ADVANCED',
            duration: '8 Weeks',
            author: 'WnCC Team',
            icon: <Brain className="w-8 h-8" />,
            color: 'from-cyan-500 to-blue-500',
            description: '> Algorithm Design\n> Data Structures\n> Problem Solving',
            tags: ['algorithms', 'complexity', 'optimization'],

            link: 'https://github.com/wncc/DSA-Bootcamp-2026'
        },
        {
            title: 'Machine Learning Projects',
            category: 'projects',
            difficulty: 'LEVEL::ADVANCED',
            duration: '4 weeks',
            author: 'WnCC Team',
            icon: <Cpu className="w-8 h-8" />,
            color: 'from-green-500 to-emerald-500',
            description: '> NLP \n> Deep Learning\n> Data Science',
            tags: ['ml', 'python', 'data-science'],

            link: 'https://github.com/wncc/Machine-Learning-LS-26'
        },
        {
            title: 'GANs and Diffusion Models',
            category: 'projects',
            difficulty: 'LEVEL::ADVANCED',
            duration: '10 hours',
            author: 'WnCC Team',
            icon: <Cpu className="w-8 h-8" />,
            color: 'from-orange-500 to-red-500',
            description: '> Generative AI\n> Deep Learning\n> Computer Vision',
            tags: ['ml', 'python', 'data-science'],

            link: 'https://github.com/wncc/Hello-FOSS-ML-Diffusivity'
        },
        {
            title: 'Parallel Programming',
            category: 'projects',
            difficulty: 'LEVEL::INTERMEDIATE',
            duration: '10 hours',
            author: 'WnCC Team',
            icon: <Cpu className="w-8 h-8" />,
            color: 'from-blue-500 to-violet-500',
            description: '> Multi-threading\n> Parallel Algorithms\n> Performance Optimization',
            tags: ['ml', 'python', 'data-science'],

            link: 'https://github.com/wncc/Hello-Foss-PyThread.cpp'
        },
        {
            title: 'C++ Programming',
            category: 'programming',
            difficulty: 'LEVEL::BEGINNER',
            duration: '10 hours',
            author: 'External',
            icon: <Code className="w-8 h-8" />,
            color: 'from-blue-500 to-indigo-500',
            description: '> Basic Syntax\n> Object Oriented Programming\n> STL Library',
            tags: ['cpp', 'programming', 'basics'],

            link: 'https://www.learncpp.com/'
        },
        {
            title: 'Python Programming',
            category: 'programming',
            difficulty: 'LEVEL::BEGINNER',
            duration: '8 hours',
            author: 'External',
            icon: <Code className="w-8 h-8" />,
            color: 'from-yellow-500 to-green-500',
            description: '> Python Basics\n> Data Structures\n> Object-Oriented Python',
            tags: ['python', 'programming', 'basics'],

            link: 'https://docs.python.org/3/tutorial/'
        },

        // Development Resources
        {
            title: 'Web Development',
            category: 'development',
            difficulty: 'LEVEL::INTERMEDIATE',
            duration: '15 hours',
            author: 'Exteral',
            icon: <Globe className="w-8 h-8" />,
            color: 'from-cyan-500 to-blue-500',
            description: '> HTML/CSS\n> JavaScript\n> React Framework',
            tags: ['web', 'frontend', 'development'],

            link: 'https://www.freecodecamp.org/'
        },
        {
            title: 'App Development',
            category: 'development',
            difficulty: 'LEVEL::INTERMEDIATE',
            duration: '20 hours',
            author: 'External',
            icon: <Smartphone className="w-8 h-8" />,
            color: 'from-green-500 to-emerald-500',
            description: '> Android Development\n> iOS Development\n> Cross-Platform',
            tags: ['mobile', 'android', 'ios'],

            link: 'https://developer.android.com/courses'
        },

        // Algorithms & Data Structures
        {
            title: 'Data Structures',
            category: 'algorithms',
            difficulty: 'LEVEL::INTERMEDIATE',
            duration: '12 hours',
            author: 'External',
            icon: <Binary className="w-8 h-8" />,
            color: 'from-purple-500 to-pink-500',
            description: '> Arrays & Linked Lists\n> Trees & Graphs\n> Hash Tables',
            tags: ['dsa', 'algorithms', 'data-structures'],

            link: 'https://www.geeksforgeeks.org/data-structures/'
        },
        {
            title: 'Algorithms',
            category: 'algorithms',
            difficulty: 'LEVEL::ADVANCED',
            duration: '15 hours',
            author: 'External',
            icon: <Brain className="w-8 h-8" />,
            color: 'from-red-500 to-orange-500',
            description: '> Sorting & Searching\n> Dynamic Programming\n> Graph Algorithms',
            tags: ['dsa', 'algorithms', 'problem-solving'],

            link: 'https://cp-algorithms.com/'
        },

        // Tools & Technologies
        {
            title: 'Git & GitHub',
            category: 'tools',
            difficulty: 'LEVEL::BEGINNER',
            duration: '5 hours',
            author: 'External',
            icon: <GitBranch className="w-8 h-8" />,
            color: 'from-orange-500 to-red-500',
            description: '> Version Control\n> Collaboration\n> Open Source',
            tags: ['git', 'github', 'version-control'],

            link: 'https://git-scm.com/book/en/v2'
        },
        {
            title: 'Linux & Shell',
            category: 'tools',
            difficulty: 'LEVEL::INTERMEDIATE',
            duration: '8 hours',
            author: 'External',
            icon: <Terminal className="w-8 h-8" />,
            color: 'from-gray-500 to-gray-700',
            description: '> Command Line\n> Shell Scripting\n> System Administration',
            tags: ['linux', 'shell', 'bash'],

            link: 'https://linuxjourney.com/'
        },

        // Development Tools
        {
            title: 'Docker & Containers',
            category: 'tools',
            difficulty: 'LEVEL::ADVANCED',
            duration: '10 hours',
            author: 'External',
            icon: <Server className="w-8 h-8" />,
            color: 'from-blue-400 to-blue-600',
            description: '> Containerization\n> Docker Compose\n> Deployment',
            tags: ['docker', 'devops', 'containers'],

            link: 'https://docs.docker.com/get-started/'
        },
        {
            title: 'Database Systems',
            category: 'development',
            difficulty: 'LEVEL::INTERMEDIATE',
            duration: '12 hours',
            author: 'External',
            icon: <Database className="w-8 h-8" />,
            color: 'from-green-400 to-emerald-600',
            description: '> SQL Basics\n> Database Design\n> Query Optimization',
            tags: ['sql', 'database', 'backend'],

            link: 'https://www.postgresqltutorial.com/'
        }
    ];


    const ResourceCard = ({ resource }) => {
        const [, setIsHovered] = useState(false);

        return (
            <Card
                className="group relative bg-gray-900/50 backdrop-blur-xl border border-gray-800 overflow-hidden"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <div className={`absolute inset-0 bg-gradient-to-r ${resource.color} opacity-5 group-hover:opacity-10 transition-opacity duration-300`} />

                {/* Cyberpunk-style corner decorations */}
                <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden">
                    <div className={`absolute top-0 right-0 w-24 h-1 bg-gradient-to-r ${resource.color}`} />
                    <div className={`absolute top-0 right-0 w-1 h-24 bg-gradient-to-b ${resource.color}`} />
                </div>
                <div className="absolute bottom-0 left-0 w-16 h-16 overflow-hidden">
                    <div className={`absolute bottom-0 left-0 w-24 h-1 bg-gradient-to-r ${resource.color}`} />
                    <div className={`absolute bottom-0 left-0 w-1 h-24 bg-gradient-to-b ${resource.color}`} />
                </div>

                <div className="relative p-6 space-y-6">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                        <div className={`p-3 rounded-xl bg-gradient-to-r ${resource.color}`}>
                            {resource.icon}
                        </div>
                        <div className="flex flex-col items-end gap-1">
                            <span className="font-mono text-sm text-gray-500">
                                {resource.difficulty}
                            </span>
                            <span className="font-mono text-sm text-gray-500">
                                DURATION::{resource.duration}
                            </span>
                        </div>
                    </div>

                    {/* Title with tech decoration */}
                    <div className="relative">
                        <h3 className={`text-2xl font-bold bg-gradient-to-r ${resource.color} text-transparent bg-clip-text`}>
                            {resource.title}
                        </h3>
                        <div className="absolute -left-2 top-1/2 w-1 h-6 -translate-y-1/2 bg-gradient-to-b from-transparent via-cyan-500 to-transparent" />
                    </div>

                    {/* Author */}
                    <div className="font-mono text-gray-400 text-sm">
                        AUTHOR::{resource.author}
                    </div>

                    {/* Description with terminal style */}
                    <div className="font-mono text-sm text-gray-400 space-y-1">
                        {resource.description.split('\n').map((line, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <span className="text-cyan-500">{line}</span>
                            </div>
                        ))}
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2">
                        {resource.tags.map((tag, index) => (
                            <span
                                key={index}
                                className={`px-3 py-1 rounded-full bg-gradient-to-r ${resource.color} bg-opacity-10 
                           text-white text-sm font-mono flex items-center gap-2`}
                            >
                                <Braces className="w-3 h-3" />
                                {tag}
                            </span>
                        ))}
                    </div>

                    {/* Metrics */}


                    {/* Action button */}
                    <button
                        onClick={() => window.open(resource.link, '_blank')}
                        className={`w-full py-2 rounded-lg bg-gradient-to-r ${resource.color} text-white 
                       font-mono flex items-center justify-center gap-2 transition-transform
                       hover:scale-[1.02] active:scale-[0.98]`}
                    >
                        <Github className="w-4 h-4" />
                        Start Learning
                    </button>
                </div>
            </Card>
        );
    };

    return (
        <div className="min-h-screen bg-gray-950 relative">
            {/* Animated circuit-like background */}
            <div className="fixed inset-0 overflow-hidden opacity-20">
                <div className="absolute w-full h-full">
                    {[...Array(20)].map((_, i) => (
                        <div
                            key={i}
                            className="absolute bg-cyan-500"
                            style={{
                                top: `${Math.random() * 100}%`,
                                left: `${Math.random() * 100}%`,
                                width: '1px',
                                height: `${Math.random() * 100}px`,
                                opacity: Math.random() * 0.5
                            }}
                        />
                    ))}
                </div>
            </div>

            {/* Header with cyberpunk styling */}
            <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-purple-500/10" />
                <div className="container my-4 mx-auto px-4 py-8">
                    <div className="flex justify-between items-center mb-8">
                        <div className="space-y-2">
                            <h1 className="text-4xl font-bold bg-gradient-to-r from-cyan-400 to-purple-500 text-transparent bg-clip-text">
                                WnCC::RESOURCES_v2.4
                            </h1>
                            <div className="font-mono text-gray-500 flex items-center gap-4">
                                <span>SYS::ACTIVE</span>
                                <span className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse" />
                                <span>{currentTime.toLocaleTimeString()}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-4 font-mono text-sm">
                            <div className="flex items-center gap-2 text-cyan-500">
                                <Activity className="w-4 h-4" />
                                <span>SYSTEM_ONLINE</span>
                            </div>
                            <div className="flex items-center gap-2 text-purple-500">
                                <Lock className="w-4 h-4" />
                                <span>SECURE_CONNECTION</span>
                            </div>
                        </div>
                    </div>

                    {/* Search and Category Filters */}
                    <div className="space-y-4 mb-8">
                        {/* Search Bar */}
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="SEARCH_RESOURCES::"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full bg-gray-900/50 border border-gray-800 rounded-lg px-4 py-3 pl-12 
                         text-white focus:outline-none focus:border-cyan-500 font-mono"
                            />
                            <Search className="w-5 h-5 text-gray-500 absolute left-4 top-1/2 transform -translate-y-1/2" />
                        </div>

                        {/* Category Buttons */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">

                        </div>
                    </div>
                </div>
            </div>

            {/* Resources Grid */}
            <div className="container mx-auto px-4 pb-12">
                <div className="grid md:grid-cols-2 gap-6">
                    {resources
                        .filter(resource =>
                            (searchQuery === '' ||
                                resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                resource.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())))
                        )
                        .map((resource, index) => (
                            <ResourceCard key={index} resource={resource} />
                        ))}
                </div>
            </div>
        </div>
    );
};


const ChessIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-5 h-5"
    >
        <path d="M8 16L10.293 13.707C10.683 13.317 10.183 12.683 9.293 12.293L7 10C6.61 9.61 6.61 8.39 7 8L9.293 5.707C9.683 5.317 10.317 5.317 10.707 5.707L13 8C13.39 8.39 13.39 9.61 13 10L10.707 12.293C10.317 12.683 10.317 13.317 10.707 13.707L13 16" />
        <path d="M16 8L13.707 10.293C13.317 10.683 13.817 11.317 14.707 11.707L17 14C17.39 14.39 17.39 15.61 17 16L14.707 18.293C14.317 18.683 13.683 18.683 13.293 18.293L11 16C10.61 15.61 10.61 14.39 11 14L13.293 11.707C13.683 11.317 13.683 10.683 13.293 10.293L11 8" />
        <circle cx="12" cy="12" r="10" />
    </svg>
);
const TeamPage = () => {
    const [selectedMember, setSelectedMember] = useState(null);
    const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
    const [expandedCards, setExpandedCards] = useState({});
    const [typingText, setTypingText] = useState({});

    const teamMembers = [
        {
            name: "Avnish Vijay More",
            role: "Manager",
            icon: <MessageCircle className="w-12 h-12 text-cyan-400" />,
            bio: "Hello everyone! I’m Avnish Vijay More, a Civil Engineering undergrad at IIT Bombay and currently serving as the Manager at the Web and Coding Club. I’m passionate about data science, machine learning, and finance, and I thrive on solving real-world challenges through technology and innovation. At the club, I focus on organizing exciting events, collaborating on impactful projects, and nurturing a strong and inclusive coding community on campus.\n\nBeyond the screen, you’ll find me on the football field, vibing to music, or diving into the world of gaming..",
            skills: ["Machine Learning", "Deep Learning", "NLP", "Football"],

            social: {
                github: "https://github.com/wejhdy",
                twitter: "",
                linkedin: "https://www.linkedin.com/in/avnish-more/",
                instagram: "https://www.instagram.com/avn1sh.__/"
            },

            imageUrl: "./Images/AVNISH.png",
        },
        {
            name: "Mithra Bijumon",
            role: "Manager",
            icon: <MessageCircle className="w-12 h-12 text-cyan-400" />,
            bio: "Hello everyone! I'm Mithra Bijumon, a third-year B.Tech student in Aerospace Engineering at IIT Bombay and currently the Manager of the Web and Coding Club, where I get to build technical projects, run coding initiatives, and help other students get started with coding. \n\nI love coding and solving problems, experimenting with new technologies, and figuring things out-whether independently or as part of a team.\n\n Outside of tech, I enjoy swimming, badminton, and exploring new places.",
            skills: [
                "Machine Learning ",
                "Web Development",
                "C++",
                "Python",
                "Computer Vision",
                "NLP",
            ],

            social: {
                github: "https://github.com/MithraBijumon",
                twitter: "",
                linkedin: "http://www.linkedin.com/in/mithra-bijumon-059104301",
                instagram: "https://www.instagram.com/mithra.bijumon/"
            },

            imageUrl: "./Images/MITHRA.png",
        },
        {
            name: "Divesh Khatwani",
            role: "Convener",
            icon: <MessageCircle className="w-12 h-12 text-cyan-400" />,
            bio: "Hi! I’m Divesh, a sophomore in the Centre for Multidisciplinary Education at IIT Bombay. \n\nI enjoy competitive programming, full-stack development, machine learning, finance, game theory, and solving problems that involve logic and mathematics. I’m always excited to learn new technologies and take on challenging projects. \n\nAs a Convener at Web and Coding Club, I enjoy organising events, public speaking, and contributing to the tech culture at IIT Bombay. I love brainstorming ideas, collaborating with people, and finding solutions to challenging problems. \n\nOutside of tech, I enjoy gaming, playing the guitar, singing, and meeting new people.",
            skills: ["C++", "JavaScript", "React.js", "Express.js", "Competitive Programming", "Machine Learning"],
            social: {
                github: "https://github.com/DV-95",
                twitter: "",
                linkedin:
                    "https://in.linkedin.com/in/divesh-khatwani-12b595275",
                instagram:
                    "https://www.instagram.com/divesh_khatwani",
            },
            imageUrl: "./Images/DIVESH.jpg",
        },
        {
            name: "Mehraj Pranami",
            role: "Convener",
            icon: <MessageCircle className="w-12 h-12 text-cyan-400" />,
            bio: "Hey! I build stuff - been doing it my whole life. I've worked on many games, hopefully someday you'll play some of them! Making interesting fun projects is what motivates me, as well as said projects reaching the audience that appreciates it. I aim to be an active member of this club and to contribute something to the legacy of this institute. Big hopes for the future, haha. AI/ML has stolen my attention this year, let's see what it entails as I learn to play with it. I also love playing games, lots of them. Excited to build great things as part of the club together. Good people working together produce great outcomes as a result. I am very curious, and excited to learn new skills. \n\nOh and by the way I'm Mehraj Pranami, from MEMS Department at IIT Bombay as an undergraduate. Always ready to have a chat.",
            skills: ["Machine Learning", "Python", "C#", "C++", "Unity Engine", "Unreal Engine 5", "Rust", "FL Studio"],
            social: {
                github: "https://github.com/Mrp1Dev",
                twitter: "",
                linkedin: "https://www.linkedin.com/in/mehraj-pranami-35162b401",
                instagram: "https://www.instagram.com/mehrajpranami"
            },
            imageUrl: "./Images/MEHRAJ.jpg",
        },

        {
            name: "Bhashkar Kumar",
            role: "Convener",
            icon: <MessageCircle className="w-12 h-12 text-cyan-400" />,
            bio: "Hello everyone! I'm Bhashkar, a second-year B.Tech student in the Energy Engineering department at IIT Bombay. I'm currently a Convener at the Web and Coding Club, where I contribute to organizing events, running technical programs, and fostering a strong coding culture on campus. I'm always eager to explore diverse technological domains and love the process of making complex concepts approachable for others. Outside of tech, I enjoy playing and watching sports cricket and football especially and you'll often find me at the gym or hunting on some good food.",
            skills: ["DSA", "C++", "Competitive Programming", "Web Development", "Python"],
            social: {
                github: "https://github.com/bhashkarcodes",
                twitter: "",
                linkedin: "https://www.linkedin.com/in/bhashkar-kumar-10b4a830b",
                instagram: "https://www.instagram.com/bhashhhkar_33"
            },
            imageUrl: "./Images/BHASKAR.jpeg",
        }
        ,
        {
            name: "Ankur Kumar",
            role: "Convener",
            icon: <MessageCircle className="w-12 h-12 text-cyan-400" />,
            bio: "Hello everyone! I'm Ankur Kumar, a second-year B.Tech student at IIT Bombay, majoring in Aerospace Engineering. I'm currently a Convener of the Web and Coding Club, where I act as PoC for projects, conduct sessions, and actively contribute to projects and activities across various domains. I'm an enthusiastic coder with a keen interest in AI/ML. Outside of tech, I enjoy all kinds of media and I enjoy playing chess, badminton, and spending time with friends.",
            skills: ["Competitive Programming", "Machine Learning", "DSA", "C++", "Python"],
            social: {
                github: "https://github.com/kumarankur-17",
                twitter: "",
                linkedin: "https://www.linkedin.com/in/ankur-kumar-583566377",
                instagram: "https://www.instagram.com/kumarankur1708"
            },
            imageUrl: "./Images/ANKUR.jpeg",
        },
        {
            name: "Ritwik Awasthi",
            role: "Convener",
            icon: <MessageCircle className="w-12 h-12 text-cyan-400" />,
            bio: "Hello, Ritwik Awasthi here! I am a second year BTech student in Metallurgical Engineering and Materials Science (MEMS) at IIT Bombay. I am currently a convenor for the Web and Coding club where I collaborate on various technical projects, plan events, run workshops and contribute towards promoting the coding culture on campus. Other than academics you might find me playing guitar, vibing to music, playing basketball, or lying on the bed and doomscrolling.",
            skills: ["SQL", "Python", "C++", "HTML", "CSS", "React"],
            social: {
                github: "https://github.com/Ritwik-01",
                twitter: "",
                linkedin: "https://www.linkedin.com/in/ritwik-awasthi-59aa79376",
                instagram: "https://www.instagram.com/ritwik_2507"
            },
            imageUrl: "./Images/RITWIK.jpg",
        },
        {
            name: "Aryan Prasad",
            role: "Convener",
            icon: <MessageCircle className="w-12 h-12 text-cyan-400" />,
            bio: "Hello everyone! I'm Aryan Prasad, a second year B.Tech student in the Department of Metallurgical Engineering and Materials Science at IIT Bombay. As a member of the Web and Coding Club, I contribute to club projects and initiatives, and conduct sessions. I'm deeply interested in problem solving and machine learning, from building models from scratch to exploring how they solve real world problems. Outside of tech, you'll find me pushing my limits in sprints and field events. Looking forward to learning, building, and collaborating with everyone!",
            skills: [
                "Python",
                "C++",
                "Machine Learning",
                "Generative AI",
                "Web Development"
            ],
            social: {
                github: "https://github.com/rain7x-dev",
                twitter: "",
                linkedin: "https://www.linkedin.com/in/aryan-prasad-238391376",
                instagram: "https://www.instagram.com/aryan7x__"
            },
            imageUrl: "./Images/ARYAN.png",
        },
        {
            name: "Harshita Sinha",
            role: "Convener",
            icon: <MessageCircle className="w-12 h-12 text-cyan-400" />,
            bio: "Hey everyone! I'm Harshita Sinha, a second-year student at IIT Bombay and currently a Convener at the Web and Coding Club (WnCC). At WnCC, I help organize workshops, bootcamps, and coding events, while also working on technical initiatives that make learning programming more engaging and accessible for students across campus. \n\n Whether it's solving challenging problems, building projects, or planning learning resources for students, I love exploring how technology can be used to create meaningful impact.\n\nOutside academics and coding, I enjoy discovering new music, watching disney movies, and spending time with friends. You'll also often find me brainstorming ideas for the next project, event, or challenge to take on",
            skills: ["Machine Learning", "Deep Learning", "DSA", "C++", "Python", "Computer Vision"],
            social: {
                github: "https://github.com/HarshitaSinha3116",
                twitter: "",
                linkedin: "https://www.linkedin.com/in/harshita-sinha-08068a373",
                instagram: "https://www.instagram.com/harshita61132"
            },
            imageUrl: "./Images/HARSHITA.jpg",
        },


        {
            name: "Jahanvi Gahlout",
            role: "Convener",
            icon: <MessageCircle className="w-12 h-12 text-cyan-400" />,
            bio: "Hello everyone! I'm Jahanvi Gahlout, a second-year Electrical Engineering undergrad at IIT Bombay. I currently serve as a Volunteer at the Web and Coding Club. I'm passionate about programming and building things, and I love diving into CTFs to sharpen my problem-solving skills. At the club, I focus on contributing to technical projects and helping organize and run events. Beyond the screen, you'll find me cycling, playing badminton, or lost in a good comic book.",
            skills: ["Java", "C++", "JavaScript", "React", "Python", "Linux"],
            social: {
                github: "https://github.com/smolhands._",
                twitter: "",
                linkedin: "http://www.linkedin.com/in/jahanvi-gahlout-481707404",
                instagram: "https://www.instagram.com/bettercallgahlout._"
            },
            imageUrl: "./Images/JAHNAVI.jpeg",
        },
        {
            name: "Dhruvilgiri Goswami",
            role: "Convener",
            icon: <MessageCircle className="w-12 h-12 text-cyan-400" />,
            bio: "Hi everyone! I’m Dhruvil Goswami, a second-year B.Tech student in Civil Engineering at IIT Bombay, and currently the Convenor of the Web and Coding Club (WNCC). I get to work on technical projects, explore new technologies, and help make coding a little less scary for everyone around me. 😄\n\nI enjoy coding, problem-solving, and building things, especially when they involve figuring out why something isn’t working five minutes before the deadline.\n\nOutside tech, I like Badminton, Cricket,playing games exploring new places, and hanging out with friends.",
            skills: ["C++", "Python", "Problem Solving", "Machine Learning"],
            social: {
                github: "https://github.com/dhruvilgoswami35-dot",
                twitter: "",
                linkedin: "https://www.linkedin.com/in/goswami-dhruvil-bb349b373",
                instagram: "https://www.instagram.com/dhruva_207"
            },
            imageUrl: "./Images/DHRUVIL.jpg",
        },
        {
            name: "Shashank Choudhary",
            role: "Convener",
            icon: <MessageCircle className="w-12 h-12 text-cyan-400" />,
            bio: "Hello everyone! I'm Shashank Choudhary, a second-year B.Tech student in Chemical Engineering at IIT Bombay. I'm currently a volunteer at the Web and Coding Club, where I try to contribute towards building a stronger coding culture on campus. I have a deep curiosity for exploring new things in tech, from generative AI to whatever's making waves in the field. I enjoy problem-solving, and don't mind spending hours working through a tricky challenge until it clicks. Beyond the screen, you'll find me playing cricket , deep into an esports tournament, or editing videos. There's always something new to try, and I'm all in for the ride.",
            skills: ["Python", "DSA", "C++", "Generative AI"],
            social: {
                github: "https://github.com/shashankc2408",
                twitter: "",
                linkedin: "https://www.linkedin.com/in/shashank-choudhary-6954a0378",
                instagram: "https://www.instagram.com/_shashank_iitb"
            },
            imageUrl: "./Images/SHASHANK.jpg",
        },
        {
            name: "Karan Singh Rajput",
            role: "Convener",
            icon: <MessageCircle className="w-12 h-12 text-cyan-400" />,
            bio: "Hello everyone! I'm  Karan, a sophomore pursuing my B.Tech in Metallurgical Engineering and Material Science at IIT Bombay. Alongside my academics, I serve as a Convener for the Web and Coding Club, where I collaborate on diverse technical projects, help organize sessions, and contribute towards building a strong coding culture in our campus. My primary interest lies in problem-solving. I enjoy building things, understanding systems deeply, and sharing that process with others. Beside coding, I like staying active through sports or catching up with friends. I enjoy the process of learning new things and always up for the next challenge.",
            skills: ["Machine Learning", "Web Development", "C++", "Python", "SQL"],
            social: {
                github: "https://github.com/KaranSingh-07",
                twitter: "",
                linkedin: "https://www.linkedin.com/in/karan-singh-295633370",
                instagram: ""
            },
            imageUrl: "./Images/KARAN.jpeg",
        },
        {
            name: "Om Dadoo",
            role: "Convener",
            icon: <MessageCircle className="w-12 h-12 text-cyan-400" />,
            bio: "Hello everyone, I am Om Dadoo 2nd year Btech in Engineering Physics, I am a convener for the web and coding club where we work to make fun tech events for all of you. I like to explore the details behind how everything works. Looking forward to exploring and learning together.",
            skills: ["Machine Learning", "Artificial Intelligence", "C++", "Python"],
            social: {
                github: "https://github.com/aom12345",
                twitter: "",
                linkedin: "https://www.linkedin.com/in/om-dadoo-872018373",
                instagram: "https://www.instagram.com/omdadoo186"
            },
            imageUrl: "./Images/OM.jpg",
        },
        {
            name: "Riya Jain",
            role: "Convener",
            icon: <MessageCircle className="w-12 h-12 text-cyan-400" />,
            bio: "Hey everyone! I’m Riya Jain, a sophomore in Applied Geophysics. I’m a Convener at the Web and Coding Club, where I love putting together events and workshops for the community. I’m someone who likes to dive deep into the fundamentals and truly understand how things work. When I'm away from my laptop, you can usually find me playing sports, painting, or out exploring random places!",
            skills: ["Machine Learning", "Computer Vision", "C++", "Python", "HTML", "JavaScript"],
            social: {
                github: "https://github.com/riyajain102",
                twitter: "",
                linkedin: "https://www.linkedin.com/in/riya-jain-4903b5388",
                instagram: "https://www.instagram.com/riyajain_102"
            },
            imageUrl: "./Images/RIYA.jpg",
        },
        {
            name: "Arham Bandi",
            role: "Convener",
            icon: <MessageCircle className="w-12 h-12 text-cyan-400" />,
            bio: "Hello, I’m Arham Bandi, a sophomore studying Mechanical Engineering at IIT Bombay. If you’ve made it this far, you’ve already spent more energy reading this bio than I probably spent writing it.\n\n But anyway, I love building things and exploring new technologies that could help solve real-world problems—or make me rich (yes, I’m talking about AI/ML). I’m generally curious about random things, enjoy reading about new stuff, and like keeping myself busy with anything that seems interesting enough. Other than that, I love watching tennis and F1, and I hope you have a great year with the WnCC team!",
            skills: ["Machine Learning", "Data Analytics", "C++", "Python", "Claude"],
            social: {
                github: "https://github.com/bandiarham07-bot",
                twitter: "",
                linkedin: "https://www.linkedin.com/in/arham-bandi",
                instagram: "https://www.instagram.com/arhambandi"
            },
            imageUrl: "./Images/ARHAM.jpg",
        }];

    useEffect(() => {
        const handleMouseMove = (e) => {
            setCursorPos({ x: e.clientX, y: e.clientY });
        };

        window.addEventListener("mousemove", handleMouseMove);
        return () => window.removeEventListener("mousemove", handleMouseMove);
    }, []);

    const simulateTyping = (memberId, text) => {
        let currentText = "";
        const words = [
            "> Loading profile...",
            "> Accessing database...",
            "> Decrypting data...",
            "> Display: OK",
        ];
        let wordIndex = 0;
        let charIndex = 0;

        const typeInterval = setInterval(() => {
            if (wordIndex === words.length) {
                clearInterval(typeInterval);
                setExpandedCards((prev) => ({ ...prev, [memberId]: true }));
                setTypingText((prev) => ({ ...prev, [memberId]: "" }));
                return;
            }

            const currentWord = words[wordIndex];
            if (charIndex === currentWord.length + 1) {
                currentText += "\n";
                wordIndex++;
                charIndex = 0;
            } else {
                currentText =
                    words.slice(0, wordIndex).join("\n") +
                    "\n" +
                    currentWord.slice(0, charIndex);
                charIndex++;
            }

            setTypingText((prev) => ({ ...prev, [memberId]: currentText }));
        }, 1);
    };

    const ChessChallenge = ({ username, rating }) => (
        <div className="mt-4 p-4 bg-gray-800 rounded-lg border border-cyan-500/20">
            <div className="flex items-center gap-3 mb-3">
                <ChessIcon className="w-5 h-5 text-cyan-400" />
                <span className="text-white font-medium">Chess Rating: {rating}</span>
            </div>
            <a
                href={`https://lichess.org/?user=${username}#friend`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-purple-500 to-cyan-500 text-white px-4 py-2 rounded-md hover:opacity-90 transition-opacity"
            >
                <ChessIcon className="w-4 h-4" />
                Challenge to Chess
            </a>
        </div>
    );

 const MemberCard = ({ member, index }) => {
    const isExpanded = expandedCards[index];

    return (
      <Card className="relative overflow-hidden bg-gray-900/50 backdrop-blur-sm transition-all duration-300 hover:shadow-xl hover:shadow-cyan-500/20">
        {/* Profile Section */}
        <div className="p-6 space-y-6">
          {/* Image and Basic Info */}
          <div className="flex items-center gap-4">
            <div className="relative w-16 h-16 rounded-full overflow-hidden ring-2 ring-cyan-500/50">
              <img
                src={member.imageUrl}
                alt={member.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">{member.name}</h3>
              <p className="text-cyan-400">{member.role}</p>
            </div>
          </div>

          {/* Bio */}
          {/* <p className="text-gray-300 text-sm">{member.bio}</p> */}

          {/* Social Links */}
          <div className="flex gap-4">
            {member.social.github && (
              <a
                href={member.social.github}
                className="text-gray-400 hover:text-cyan-400 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Github className="w-5 h-5" />
              </a>
            )}
            {member.social.instagram && (
              <a
                href={member.social.instagram}
                className="text-gray-400 hover:text-cyan-400 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Instagram className="w-5 h-5" />
              </a>
            )}
            {member.social.linkedin && (
              <a
                href={member.social.linkedin}
                className="text-gray-400 hover:text-cyan-400 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            )}
          </div>

          {!isExpanded && (
            <button
              onClick={() => {
                simulateTyping(index, member.name);
              }}
              className="w-full bg-gray-800 text-cyan-400 px-4 py-2 rounded-md font-mono text-sm hover:bg-gray-700 transition-colors"
            >
              $ ./view-profile {member.name.toLowerCase().replace(" ", "-")}
            </button>
          )}
        </div>

        {/* Terminal Output */}
        {typingText[index] && (
          <div className="px-6 pb-4">
            <pre className="font-mono text-xs text-cyan-400 whitespace-pre-wrap">
              {typingText[index]}
            </pre>
          </div>
        )}

      {/* Details Section */}
<div
  className={`border-t border-cyan-500/20 transition-all duration-500 ${
    isExpanded ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
  } overflow-hidden`}
>
  <div className="p-6 h-[384px] flex flex-col justify-between">
    
    {/* Top Section: About & Skills */}
    <div className="space-y-6">
      {/* Bio (with scroll) */}
      <div className="max-h-40 overflow-y-auto pr-2 custom-scroll">
        <h4 className="text-sm font-semibold text-cyan-400 mb-3">About</h4>
        <p className="text-gray-300 text-sm">{member.bio}</p>
      </div>

      {/* Skills */}
      <div>
        <h4 className="text-sm font-semibold text-cyan-400 mb-3">Skills</h4>
        <div className="flex flex-wrap gap-2">
          {member.skills.map((skill, index) => (
            <span
              key={index}
              className="bg-cyan-500/10 text-cyan-400 px-2 py-1 rounded-md text-xs"
            >
              {skill}
            </span>
          ))}
        </div>
      </div>
    </div>

    {/* Bottom Section: View Profile Button */}
    <button
      onClick={() => setSelectedMember(member)}
      className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-4 py-2 rounded-md hover:opacity-90 transition-opacity"
    >
      View Full Profile
    </button>
  </div>
</div>


      </Card>
    );
  };

  return (
    <div className="space-y-12 relative">
      {/* Cursor gradient follow effect */}
      <div
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          background: `radial-gradient(600px at ${cursorPos.x}px ${cursorPos.y}px, rgba(103, 232, 249, 0.15), transparent 80%)`,
        }}
      />

      <div className="text-center relative">
        <h2 className="text-5xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 text-transparent bg-clip-text mb-6">
          Meet Our Team
        </h2>
        <p className="text-xl text-gray-300 max-w-2xl mx-auto">
          The brilliant minds behind WnCC's success
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {teamMembers.map((member, index) => (
          <MemberCard key={index} member={member} index={index} />
        ))}
      </div>

      {/* Selected member modal */}
      {selectedMember && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-900 p-8 rounded-2xl max-w-2xl w-full mx-4 relative">
            <button
              onClick={() => setSelectedMember(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-shrink-0">
                <div className="w-48 h-48 rounded-xl overflow-hidden">
                  <img
                    src={selectedMember.imageUrl}
                    alt={selectedMember.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-grow">
                <div className="flex items-center gap-4 mb-4">
                  {selectedMember.icon}
                  <div>
                    <h3 className="text-2xl font-bold text-white">
                      {selectedMember.name}
                    </h3>
                    <p className="text-cyan-400">{selectedMember.role}</p>
                  </div>
                </div>

                <p className="text-gray-300 mb-6">{selectedMember.bio}</p>

                <div>
                  <h4 className="text-lg font-semibold text-white mb-3">
                    Skills
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedMember.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="bg-cyan-500/20 text-cyan-400 px-3 py-1 rounded-full text-sm"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {selectedMember.chess && (
                  <div className="mt-6">
                    <ChessChallenge
                      username={selectedMember.chess.lichessUsername}
                      rating={selectedMember.chess.rating}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
const ContactPage = () => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        subject: "",
        message: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [codeString, setCodeString] = useState("");
    // const [currentField, setCurrentField] = useState(null);

    useEffect(() => {
        const code = "> const contact = await WnCC.connect(you);";
        let index = 0;
        const interval = setInterval(() => {
            setCodeString(code.slice(0, index));
            index++;
            if (index > code.length) {
                index = 0;
            }
        }, 150);
        return () => clearInterval(interval);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        setTimeout(() => {
            setIsSubmitting(false);
            setFormData({ name: "", email: "", subject: "", message: "" });
            alert("Message sent successfully!");
        }, 1000);
    };

    // const renderFieldValue = (field, value) => {
    //     if (!value) return "";
    //     if (currentField === field) {
    //         return `"${value}█"`;
    //     }
    //     return `"${value}"`;
    // };

    return (
        <div className="space-y-12 relative">
            <div className="hidden md:block absolute -right-4 top-10 text-cyan-500/20">
                <Code2 size={120} />
            </div>
            <div className="hidden md:block absolute -left-4 bottom-10 text-cyan-500/20">
                <Terminal size={120} />
            </div>

            <div className="text-center relative">
                <div className="font-mono text-cyan-400 mb-4 animate-pulse">
                    {codeString}
                </div>
                <h2 className="text-5xl font-bold bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 text-transparent bg-clip-text mb-6">
                    Connect with WnCC
                </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
                <Card className="p-6 bg-gray-900/50 backdrop-blur border-cyan-500/20">
                    <h3 className="text-2xl font-bold text-cyan-400 mb-6 flex items-center gap-2">
                        <Terminal className="w-6 h-6" /> Initialize Connection
                    </h3>
                    <div className="font-mono text-sm text-gray-400 mb-4">
                        {/* Please fill in your details below */}
                    </div>
                    <form onSubmit={handleSubmit} className="space-y-6 font-mono">
                        <div className="bg-gray-900 p-4 rounded border border-cyan-500/20">
                            <div className="text-cyan-400">const userDetails = {"{"}</div>
                            <div className="pl-4 space-y-4">
                                <div>
                                    <label className="text-blue-400">name:</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) =>
                                            setFormData({ ...formData, name: e.target.value })
                                        }
                                        // onFocus={() => setCurrentField("name")}
                                        // onBlur={() => setCurrentField(null)}
                                        className="bg-transparent border-none outline-none text-green-400 ml-2 w-64"
                                        required
                                        placeholder="type your name..."
                                    />
                                    <span className="text-gray-500">,</span>
                                </div>
                                <div>
                                    <label className="text-blue-400">email:</label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) =>
                                            setFormData({ ...formData, email: e.target.value })
                                        }
                                        // onFocus={() => setCurrentField("email")}
                                        // onBlur={() => setCurrentField(null)}
                                        className="bg-transparent border-none outline-none text-green-400 ml-2 w-64"
                                        required
                                        placeholder="enter your email..."
                                    />
                                    <span className="text-gray-500">,</span>
                                </div>
                                <div>
                                    <label className="text-blue-400">subject:</label>
                                    <input
                                        type="text"
                                        value={formData.subject}
                                        onChange={(e) =>
                                            setFormData({ ...formData, subject: e.target.value })
                                        }
                                        // onFocus={() => setCurrentField("subject")}
                                        // onBlur={() => setCurrentField(null)}
                                        className="bg-transparent border-none outline-none text-green-400 ml-2 w-64"
                                        required
                                        placeholder="message subject..."
                                    />
                                    <span className="text-gray-500">,</span>
                                </div>
                                <div>
                                    <label className="text-blue-400">message:</label>
                                    <textarea
                                        value={formData.message}
                                        onChange={(e) =>
                                            setFormData({ ...formData, message: e.target.value })
                                        }
                                        // onFocus={() => setCurrentField("message")}
                                        // onBlur={() => setCurrentField(null)}
                                        className="bg-transparent border-none outline-none text-green-400 ml-2 w-full h-24 resize-none"
                                        required
                                        placeholder="type your message..."
                                    />
                                </div>
                            </div>
                            <div className="text-cyan-400">{"}"}</div>
                        </div>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white py-3 rounded hover:from-cyan-600 hover:to-blue-700 transition-all duration-300 disabled:opacity-50"
                        >
                            {isSubmitting
                                ? "> executing..."
                                : "> WnCC.sendMessage(userDetails)"}
                        </button>
                    </form>
                </Card>

                <div className="space-y-6">
                    <Card className="p-6 bg-gray-900/50 backdrop-blur border-cyan-500/20">
                        <h3 className="text-xl font-bold text-cyan-400 mb-4 flex items-center gap-2">
                            <Globe className="w-5 h-5" /> Base Location
                        </h3>
                        <p className="text-gray-300 font-mono">
                            Web and Coding Club
                            <br />
                            Student Activity Center
                            <br />
                            IIT Bombay, Powai
                            <br />
                            Mumbai - 400076
                        </p>
                    </Card>

                    <Card className="p-6 bg-gray-900/50 backdrop-blur border-cyan-500/20">
                        <h3 className="text-xl font-bold text-cyan-400 mb-4 flex items-center gap-2">
                            <Database className="w-5 h-5" /> Contact Database
                        </h3>
                        <div className="space-y-3 font-mono">
                            <p className="text-gray-300">email: wncc@iitb.ac.in</p>
                            <p className="text-gray-300">managers: Avnish Vijay Morey, Mithra Bijumon</p>
                            <p className="text-gray-300">phone: +91 9167339950/8762070856</p>
                        </div>
                    </Card>

                    <Card className="p-6 bg-gray-900/50 backdrop-blur border-cyan-500/20">
                        <h3 className="text-xl font-bold text-cyan-400 mb-4 flex items-center gap-2">
                            <Code2 className="w-5 h-5" /> Social Network
                        </h3>
                        <div className="flex gap-4">
                            <a
                                href="https://github.com/wncc"
                                className="text-gray-300 hover:text-cyan-400 transition-colors"
                            >
                                <Github className="w-6 h-6" />
                            </a>
                            <a
                                href="https://www.instagram.com/wncc.iitb/"
                                className="text-gray-300 hover:text-cyan-400 transition-colors"
                            >
                                <Instagram className="w-6 h-6" />
                            </a>
                            <a
                                href="https://www.linkedin.com/company/wncc-iitb/posts/?feedView=all"
                                className="text-gray-300 hover:text-cyan-400 transition-colors"
                            >
                                <Linkedin className="w-6 h-6" />
                            </a>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};
const WebsitePreview = () => {
    const [currentPage, setCurrentPage] = useState("Home");

    const hasInitialized = useRef(false);
    
    useEffect(() => {
        if (hasInitialized.current) return;
        hasInitialized.current = true;
        
        // Check for accessid parameter and redirect to loading
        const params = new URLSearchParams(window.location.search);
        const accessid = params.get('accessid');
        
        if (accessid) {
            setCurrentPage('loading');
            return;
        }
        
        // Set initial page based on URL
        const path = window.location.pathname;
        const pathMap = {
            '/': 'Home',
            '/events': 'Events',
            '/resources': 'Resources',
            '/team': 'Team', 
            '/contact': 'Contact',
            '/hello-foss': 'Hello FOSS',
            '/cc': 'Coding Circuit',
            '/quiz': 'Quiz',
            '/loading': 'loading'
        };
        const initialPage = pathMap[path] || 'Home';
        setCurrentPage(initialPage);
    }, []);

    useEffect(() => {
        // Update URL when page changes (except loading)
        if (currentPage !== 'loading') {
            const pageMap = {
                'Home': '/',
                'Events': '/events',
                'Resources': '/resources', 
                'Team': '/team',
                'Contact': '/contact',
                'Hello FOSS': '/hello-foss',
                'Coding Circuit': '/cc',
                'Quiz': '/quiz'
            };
            if (pageMap[currentPage] && window.location.pathname !== pageMap[currentPage]) {
                window.history.pushState({}, '', pageMap[currentPage]);
            }
        }
    }, [currentPage]);

    // Get the appropriate page component based on current selection
    const getPageComponent = () => {
        switch(currentPage) {
            case "Home":
                return <HomePage />;
            case "Events":
                return <EventsPage />;
            case "Resources":
                return <ResourcesPage />;
            case "Team":
                return <TeamPage />;
            case "Contact":
                return <ContactPage />;
            case "Hello FOSS":
                return <HelloFOSS />;
            case "Quiz":
                return <Quiz />;
            case "loading":
                return <SSOLoading />;
            case "Coding Circuit":
                return <CodingCircuit />;
            default:
                return <HomePage />;
        }
    };

    return (
        <div className="min-h-screen bg-gray-950 text-white relative">
            <ParticleEffect />
            <Navbar currentPage={currentPage} setCurrentPage={setCurrentPage} />
            
            <main className="container mx-auto px-4 py-8">
                {getPageComponent()}
            </main>
            
            <Footer />
        </div>
    );
};

export default WebsitePreview;
